import { adjustmentType, SkuType } from './enums';
import { BlueprintInstanceType } from '../shared.enums';
import { NotificationType } from '../notifications/enums';
import { promotionalSkuData } from './promotional-sku.data';

import { Publish } from '../../decorators';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';
import { type DiscountRule, PromotionalSkuAssetData, PromotionalSkuData, PromotionalSkuDataIn } from './types';
import { purgeNotifcationsCfn, validateCfn } from './functions';

export class PromotionalSku extends KitchenDesignBase<PromotionalSkuDataIn> {
	#sku: string;
	#description: string;
	#priceAdjustment: number;
	#adjustmentType: adjustmentType;
	#skuType: SkuType;
	#requiredSkus: string[];
	#discountRules: Array<DiscountRule>;
	#assetData: PromotionalSkuAssetData;
	#quantity: number;
	#totalAdjustment: number;
	#ruleBreakdown: Record<string, number>;
	#hsProductId: string | null;

	constructor (options: KitchenDesignOptions<PromotionalSkuDataIn> = {}) {
		super(BlueprintInstanceType.PromotionalSku, options);
		const { dataIn = null } = options;
		const data = promotionalSkuData(dataIn);

		this._id = data.id;
		this.#sku = data.sku;
		this.#description = data.description;
		this.#priceAdjustment = data.priceAdjustment;
		this.#adjustmentType = data.adjustmentType;
		this.#skuType = data.skuType ?? SkuType.Promotion;
		this.#requiredSkus = data.requiredSkus;
		this.#discountRules = data.discountRules ?? [];
		this.#quantity = data.quantity;
		this.#ruleBreakdown = {};
		this.#hsProductId = data.hsProductId ?? null;
		// this.#assetData = data.assetData;

		this.subscribe('panel_overhang', 'updated', this.validate);
		this.subscribe('panel_size', 'updated', this.validate);
		this.subscribe('panel_item_group', 'moved', this.validate);
		this.subscribe('panel_trims', 'reset', this.validate);

		this.setInitialized();
	}

	@Publish('promotional_sku', 'updated')
	set sku (sku: string) { this.#sku = sku; }
	get sku (): string { return this.#sku }

	@Publish('promotional_sku_description', 'updated')
	set description (description: string) { this.#description = description; }
	get description (): string { return this.#description }

	get allowMultiple (): boolean { return this.#assetData?.allowMultiple ?? null }
	get skuType (): SkuType { return this.#skuType }
	get isDiscount (): boolean { return this.#skuType === SkuType.Discount }
	get isFinal (): boolean { return this.#skuType === SkuType.Final }
	get requiredSkus (): string[] { return this.#requiredSkus ?? null }
	get discountRules (): Array<DiscountRule> { return this.#discountRules }
	get hasDiscountRules (): boolean { return this.#discountRules?.length > 0 }
	get isValid (): boolean {
		const notifications = this.getNotications();
		return notifications.every((notification) => notification.type === NotificationType.Info || notification.acceptedBy !== null);
	}
	get adjustmentType (): adjustmentType { return this.#adjustmentType }

	@Publish('promotional_sku_price', 'updated')
	set priceAdjustment (priceAdjustment: number) { this.#priceAdjustment = priceAdjustment; }
	get priceAdjustment (): number { return this.#priceAdjustment }

	get totalAdjustment (): number { return this.#totalAdjustment }
	set totalAdjustment (totalAdjustment: number) { this.#totalAdjustment = totalAdjustment; }

	get ruleBreakdown (): Record<string, number> { return this.#ruleBreakdown }
	set ruleBreakdown (breakdown: Record<string, number>) { this.#ruleBreakdown = breakdown; }

	@Publish('promotional_sku_quantity', 'updated')
	set quantity (quantity: number) { this.#quantity = quantity; }
	get quantity (): number { return this.#quantity }

	get hsProductId (): string | null { return this.#hsProductId }

	public validate (): void {
		purgeNotifcationsCfn(this);
		validateCfn(this);
	}

	// serialize
	public serialize (): PromotionalSkuData {
		return {
			id: this._id,
			sku: this.#sku,
			description: this.#description,
			priceAdjustment: this.#priceAdjustment,
			adjustmentType: this.#adjustmentType,
			skuType: this.#skuType,
			requiredSkus: this.#requiredSkus,
			discountRules: this.#discountRules,
			hsProductId: this.#hsProductId,
			quantity: this.#quantity
		};
	}
}
